import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Coins, Play, History, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import ArcadeBoard, { FAMILY_ICON, DEFAULT_ACTION } from "@/pages/arcade/ArcadeBoard";

const GameTile = ({ g, i, active, onPick }) => {
  const I = FAMILY_ICON[g.family] || Gamepad2;
  return (
    <button data-testid={`arcade-game-${g.id}`} onClick={() => onPick(g)} className={`card-cr group relative flex flex-col items-start gap-1.5 overflow-hidden text-left transition-colors hover:border-cr-gold/60 ${active ? "border-cr-gold bg-cr-gold/5" : ""}`}>
      <div className="relative aspect-[3/2] w-full overflow-hidden">
        <img src={`/brand/arcade/games/${g.id}.jpg`} alt={g.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="px-3 pb-3">
      <span className="block font-display font-bold leading-tight">{g.name}</span>
      <span className="flex items-center gap-1.5 text-xs text-cr-muted"><I className="h-3.5 w-3.5 text-cr-gold" />{g.family_name}</span>
      <span className="mt-1 flex items-center gap-1 text-[11px] font-bold uppercase text-cr-gold">Play<Play className="h-3 w-3 fill-current" /></span>
      </div>
    </button>
  );
};

const Panel = ({ game, data, onPlayed }) => {
  const { user, wallet } = useAuth();
  const [bet, setBet] = useState(20);
  const [action, setAction] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAction(DEFAULT_ACTION[game.family] || {}); setResult(null); }, [game]);
  const coins = wallet?.coins || 0;
  const play = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/arcade/play", { game_id: game.id, bet, action }); setResult(r); onPlayed(r); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="arcade-panel" className="card-gold overflow-hidden">
      <img src={`/brand/arcade/games/${game.id}.jpg`} alt={game.name} className="aspect-[3/2] w-full object-cover" />
      <div className="p-5">
      <p className="text-xs font-bold uppercase tracking-widest text-cr-gold">{game.family_name}</p>
      <h2 data-testid="arcade-selected-name" className="mt-1 text-2xl font-bold">{game.name}</h2>
      <p className="mb-4 text-sm text-cr-muted">{game.description}</p>
      <ArcadeBoard family={game.family} action={action} setAction={setAction} result={result} />
      <div className="mt-5 flex items-center justify-between text-sm"><span>Bet</span><b data-testid="arcade-bet-value" className="text-cr-gold">{bet} coins</b></div>
      <input data-testid="arcade-bet-slider" type="range" min={data.min_bet} max={data.max_bet} step="10" value={bet} onChange={(e) => setBet(Number(e.target.value))} className="w-full accent-[#FFC928]" />
      {!user ? <Link to="/login" data-testid="arcade-login-btn" className="btn-gold mt-4 w-full py-3">Login to play</Link> : (
        <button data-testid="arcade-play-btn" disabled={busy || coins < bet} onClick={play} className="btn-gold mt-4 w-full py-3">{busy ? "Playing…" : coins < bet ? "Not enough coins" : "Play round"}<Play className="h-4 w-4 fill-current" /></button>
      )}
      {user && coins < bet && <p className="mt-2 text-center text-xs text-cr-muted">Coins are credited by your admin.</p>}
      {result && (
        <div data-testid="arcade-result" className={`mt-4 rounded-lg border p-4 text-center ${result.payout > 0 ? "border-cr-success/50 bg-cr-success/10" : "border-cr-line bg-cr-elevated"}`}>
          <p className="text-sm">{result.outcome}</p>
          <p data-testid="arcade-result-payout" className={`font-display text-2xl font-black ${result.payout > 0 ? "text-cr-success" : "text-cr-muted"}`}>{result.payout > 0 ? `+${result.payout} coins` : "No win"}</p>
          <p className="text-xs text-cr-muted">Bet {result.bet} · Net {result.net >= 0 ? "+" : ""}{result.net} · Balance {result.coins_after}</p>
        </div>
      )}
      </div>
    </div>
  );
};

const Recent = ({ rows }) => (
  <div className="card-cr p-5">
    <p className="mb-3 flex items-center gap-2 font-semibold"><History className="h-4 w-4 text-cr-gold" />Recent rounds</p>
    {rows.length === 0 ? <p className="text-sm text-cr-muted">Your rounds will appear here.</p> : rows.slice(0, 8).map((h) => (
      <div key={h.round_id} className="flex justify-between border-b border-cr-line py-2 text-sm last:border-0"><span className="truncate">{h.game_name}</span><b className={h.payout > 0 ? "text-cr-success" : "text-cr-muted"}>{h.payout > 0 ? `+${h.payout}` : `-${h.bet}`}</b></div>
    ))}
  </div>
);

export default function Arcade() {
  const { user, wallet, refreshWallet } = useAuth();
  const [data, setData] = useState(null);
  const [family, setFamily] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [hist, setHist] = useState([]);
  const [sp] = useSearchParams();
  useEffect(() => { api.get("/arcade/games").then((r) => { setData(r.data); setSel(r.data.games.find((g) => g.id === sp.get("play")) || r.data.games[0]); }); }, [sp]);
  useEffect(() => { if (user) api.get("/arcade/history").then((r) => setHist(r.data)).catch(() => {}); }, [user]);
  const shown = useMemo(() => (data?.games || []).filter((g) => (family === "all" || g.family === family) && g.name.toLowerCase().includes(q.toLowerCase())), [data, family, q]);
  const played = (r) => { setHist((h) => [r, ...h]); refreshWallet(); };

  return (
    <main data-testid="arcade-page" className="mx-auto max-w-7xl px-5 py-10">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-cr-gold">{data?.total || 120} games</p>
          <h1 className="text-4xl font-black uppercase">Golden <span className="gold-text">Arcade</span></h1>
          <p className="mt-2 max-w-2xl text-sm text-cr-muted">Play with your coins. Results are generated on the server instantly. Add coins by UPI and withdraw winnings to your UPI anytime.</p>
        </div>
        {user && <div data-testid="arcade-coin-balance" className="flex items-center gap-2 rounded-xl border border-cr-gold/40 bg-cr-surface px-5 py-3"><Coins className="h-6 w-6 text-cr-gold" /><span className="font-display text-2xl font-black">{(wallet?.coins || 0).toLocaleString("en-IN")}</span><span className="text-xs text-cr-muted">coins</span></div>}
      </div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          {["all", ...(data?.families || [])].map((f) => <button key={f} data-testid={`arcade-filter-${f}`} onClick={() => setFamily(f)} className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${family === f ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line text-cr-ink/80"}`}>{f}</button>)}
        </div>
        <div className="relative md:w-72"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" /><input data-testid="arcade-search-input" className="input-cr pl-10" placeholder="Search games" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>
      {!data ? <Skeleton className="h-96 bg-cr-elevated" /> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div>
            <p data-testid="arcade-count" className="mb-3 text-xs font-bold uppercase text-cr-muted">{shown.length} games · select to play</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">{shown.map((g, i) => <GameTile key={g.id} g={g} i={i} active={sel?.id === g.id} onPick={(x) => { setSel(x); if (window.innerWidth < 1024) document.getElementById("arcade-side")?.scrollIntoView({ behavior: "smooth" }); }} />)}</div>
          </div>
          <aside id="arcade-side" className="order-first space-y-5 lg:order-none lg:sticky lg:top-24 lg:self-start">
            {sel && <Panel game={sel} data={data} onPlayed={played} />}
            {user && <Recent rows={hist} />}
          </aside>
        </div>
      )}
    </main>
  );
}
